import { useState } from 'react'
import { CategoryIcon, reminderLook } from '../components/CategoryIcon'
import { api } from '../lib/api'
import { formatDistance } from '../lib/location'
import { useStore } from '../lib/store'
import './Household.css'

export function HouseholdScreen() {
  const { households, reminders, categories, user, refreshHouseholds, refresh } = useStore()
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const household = households[0] ?? null
  const shared = reminders.filter(
    (r) => r.household_id && r.household_id === household?.id && !r.completed_at,
  )

  async function run(fn: () => Promise<unknown>, ok?: string) {
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      await fn()
      await refreshHouseholds()
      await refresh()
      if (ok) setNote(ok)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function ping(reminderId: string, userId: string, who: string) {
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      const res = await api.ping(household!.id, reminderId, userId)
      setNote(
        res.delivered > 0
          ? `Pinged ${who} on ${res.delivered} device${res.delivered === 1 ? '' : 's'}.`
          : res.push_configured
            ? `${who} has no device registered for push yet — they need to open Nudge and enable notifications.`
            : 'Push is not configured on the server, so nothing was sent.',
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <header className="nav">
        <h1>Household</h1>
      </header>

      <div className="scroll">
        {error && <p className="banner banner--error" role="alert">{error}</p>}
        {note && <p className="banner" role="status">{note}</p>}

        {!household ? (
          <div className="stack">
            <div className="glass hh__panel rise">
              <h2>Start a household</h2>
              <p className="hint">
                Share an errand list. Whoever is closest to the store gets the nudge.
              </p>
              <input
                className="hh__input"
                value={name}
                placeholder="The Cornwells"
                onChange={(e) => setName(e.target.value)}
              />
              <button
                className="btn btn--primary"
                disabled={busy || !name.trim()}
                onClick={() => run(() => api.createHousehold(name.trim()))}
              >
                Create
              </button>
            </div>

            <div className="glass hh__panel rise" style={{ animationDelay: '80ms' }}>
              <h2>Join one</h2>
              <p className="hint">Enter the six-character invite code.</p>
              <input
                className="hh__input hh__input--code"
                value={code}
                maxLength={6}
                placeholder="AB3XK9"
                onChange={(e) => setCode(e.target.value.toUpperCase())}
              />
              <button
                className="btn btn--ghost"
                disabled={busy || code.length < 4}
                onClick={() => run(() => api.joinHousehold(code))}
              >
                Join
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="hh__head glass rise">
              <div>
                <h2>{household.name}</h2>
                <p className="hint">
                  {household.members.length} member
                  {household.members.length === 1 ? '' : 's'}
                </p>
              </div>
              <div className="hh__avatars">
                {household.members.map((m) => (
                  <span key={m.user_id} title={m.display_name}>
                    {m.display_name.slice(0, 1).toUpperCase()}
                  </span>
                ))}
              </div>
            </div>

            <div className="hh__invite glass rise" style={{ animationDelay: '70ms' }}>
              <div>
                <p className="hint">Invite code</p>
                <strong>{household.invite_code}</strong>
              </div>
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  void navigator.clipboard?.writeText(household.invite_code)
                  setNote('Invite code copied.')
                }}
              >
                Copy
              </button>
            </div>

            {/* The email carries the same code shown above — this is a delivery
                mechanism, not a second way in. */}
            <div className="hh__email glass rise" style={{ animationDelay: '90ms' }}>
              <p className="hint">Or send it to them</p>
              <div className="hh__email-row">
                <input
                  type="email"
                  placeholder="them@example.com"
                  value={inviteEmail}
                  autoComplete="off"
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' || !inviteEmail.trim()) return
                    void run(
                      () => api.inviteToHousehold(household.id, inviteEmail.trim()),
                      `Invite sent to ${inviteEmail.trim()}.`,
                    ).then(() => setInviteEmail(''))
                  }}
                />
                <button
                  className="btn btn--sm"
                  disabled={busy || !inviteEmail.trim()}
                  onClick={() =>
                    void run(
                      () => api.inviteToHousehold(household.id, inviteEmail.trim()),
                      `Invite sent to ${inviteEmail.trim()}.`,
                    ).then(() => setInviteEmail(''))
                  }
                >
                  Send
                </button>
              </div>
            </div>

            <h2 className="section-label">Shared board</h2>
            <div className="stack">
              {shared.map((r, i) => {
                const closest = r.closest_member
                const cat = reminderLook(r, categories)
                return (
                  <div
                    key={r.id}
                    className="hh__card glass rise"
                    style={{ animationDelay: `${i * 55}ms` }}
                  >
                    <CategoryIcon icon={cat.icon} color={cat.color} />
                    <div className="hh__body">
                      <strong>{r.title}</strong>
                      <span>{r.merchant_label}</span>
                      {closest && (
                        <span className="hh__closest">
                          {closest.is_you ? 'You are' : `${closest.display_name} is`} closest
                          {' · '}
                          {formatDistance(closest.distance_m)}
                        </span>
                      )}
                      {!closest && (
                        <span className="hh__closest hh__closest--none">
                          No one has reported a position recently
                        </span>
                      )}
                    </div>
                    {closest && !closest.is_you && (
                      <button
                        className="btn btn--amber btn--sm"
                        disabled={busy}
                        onClick={() =>
                          ping(r.id, closest.user_id, closest.display_name)
                        }
                      >
                        Ping
                      </button>
                    )}
                  </div>
                )
              })}

              {shared.length === 0 && (
                <div className="empty">
                  <h2>Board is empty</h2>
                  <p>
                    Add a reminder and choose &ldquo;{household.name}&rdquo; under
                    &ldquo;Share with&rdquo; to put it here.
                  </p>
                </div>
              )}
            </div>

            <p className="hint hh__note">
              Pings are delivered by Web Push, so they arrive even with Nudge closed —
              on Android, and on iOS once Nudge is installed to the home screen.
              {user?.tier === 'free' && ' Sharing is a Family-plan feature at launch.'}
            </p>
          </>
        )}
      </div>
    </>
  )
}
